import { useEffect } from 'react';
import {
  RouterProvider,
  createBrowserRouter,
  redirect,
  useLocation,
  type RouteObject,
} from 'react-router-dom';
import { ROUTABLE } from './route-manifest.js';
import { DEFERRED_GROUPS, deferredRoutes, founderPathsFor } from './business-os.js';
import { FOUNDER_ORIGIN, appHost, type AppHost } from './host.js';
import { NotFound, RouteError } from './NotFound.js';
import { FRAME_BOX } from './responsive/frame.js';

/**
 * The composition root for the client.
 *
 * Each work package exports a `RouteObject[]` from its own `routes/<group>/routes.tsx`
 * and this file mounts them. Nothing here reaches into a package's internals, and no
 * package edits this file — with several agents writing routes concurrently, a shared
 * router file would be the one guaranteed merge conflict.
 *
 * ── GROUPS ARE FOUND, NOT LISTED ────────────────────────────────────────────────
 * This file used to import every group by name, so a new group meant an edit here. The
 * customer's groups are now collected with `import.meta.glob`: a new directory under
 * `routes/` with a `routes.tsx` that exports a `…Routes` array is mounted by existing,
 * with nothing to register. The Business OS groups are found the same way by
 * `business-os.ts`, through their `paths.ts`.
 *
 * ── TWO BUNDLES, AND THE ONE LIST THAT KEEPS THEM TWO ───────────────────────────
 * The customer's groups are imported EAGERLY: they are the first-load bundle, and a
 * person on 2G must not wait for a second download between a tap and a drawn screen.
 * The Business OS — ফাউন্ডার mode, vendor, ops, HR, finance, corporate, projects, the
 * legal vault, ops money and the catalogue admin — is loaded through `business-os.ts`
 * with `import()`, because a customer never opens any of it and was paying 246 kB to
 * carry it.
 *
 * An eager glob has to be a literal, so it cannot ask "every group without a paths.ts".
 * The negative patterns in `CUSTOMER_MODULES` are therefore the one list in this file,
 * and it is guarded twice: at runtime, a group `business-os.ts` defers is never ALSO
 * mounted eagerly, whatever the list says, so a missed entry costs bundle size and never
 * a double route; and `__tests__/app-shell.test.ts` fails on the missed entry, so the
 * bundle size is not lost either. That one line is the only edit a NEW Business OS group
 * makes outside its own directory; the two created empty for later waves (ops-money,
 * catalogue-admin) are already in it.
 *
 * ── THREE HOSTS, ONE BUILD ──────────────────────────────────────────────────────
 * `host.ts` names the product this hostname serves (founder decision 2026-09-18):
 *
 *   customer  oprappo.com, www, app. The customer's groups, the vendor console, and a
 *             redirect to founder.oprappo.com for every Business OS path — the whole
 *             path, query and fragment kept, so a bookmarked /founder/approvals lands on
 *             /founder/approvals.
 *   founder   founder.oprappo.com. Sign-in (the onboarding group, which owns /auth/*)
 *             and every Business OS group except vendor. `/` is /founder/enter.
 *   both      localhost, 127.0.0.1, *.pages.dev. Everything, redirecting nothing, so the
 *             visual, CSP and responsive harnesses score every frame in one run and a
 *             preview deployment reviews its own branch.
 *
 * Every host ends in the same two places: a `*` route that renders the Bangla not-found
 * screen, and one root `errorElement` that renders the Bangla error screen — and reloads
 * once when the failure was a chunk a deploy replaced. See `NotFound.tsx`.
 */

/** A `routes.tsx` module: its `…Routes` exports are its route list. */
type RouteModule = Record<string, unknown>;

/**
 * Every customer group, eagerly. The negative patterns are the Business OS groups — every
 * directory with a `paths.ts` — which `business-os.ts` loads lazily instead.
 */
const CUSTOMER_MODULES = import.meta.glob<RouteModule>(
  [
    './routes/*/routes.tsx',
    '!./routes/catalogue-admin/routes.tsx',
    '!./routes/corporate/routes.tsx',
    '!./routes/finance/routes.tsx',
    '!./routes/founder/routes.tsx',
    '!./routes/hr/routes.tsx',
    '!./routes/legal/routes.tsx',
    '!./routes/ops/routes.tsx',
    '!./routes/ops-money/routes.tsx',
    '!./routes/projects/routes.tsx',
    '!./routes/vendor/routes.tsx',
  ],
  { eager: true },
);

/** `./routes/account-notifications/routes.tsx` → `account-notifications`. */
function groupOf(key: string): string {
  const parts = key.split('/');
  return parts[parts.length - 2] ?? key;
}

/**
 * A group's routes: every named export ending in `Routes` that is an array.
 *
 * By NAME, not "every array": a `routes.tsx` may re-export a module's API (notifications
 * and provider do), and an array of anything else must never be read as routes. The
 * `default` export is skipped because where one exists it is the same array again.
 */
function routesOf(module: RouteModule): RouteObject[] {
  const lists = Object.entries(module)
    .filter(([name, value]) => name !== 'default' && /Routes$/.test(name) && Array.isArray(value))
    .map(([, value]) => value as RouteObject[]);
  return [...new Set(lists)].flat();
}

/**
 * The customer's groups by directory name, in directory-name order.
 *
 * Route ORDER does not decide a match in React Router 6 — it ranks by specificity — and
 * `app-shell.test.ts` asserts no two groups mount the same path, which is the one case
 * where order would matter. Sorting is for a stable table, not for correctness.
 */
export const CUSTOMER_GROUPS: Readonly<Record<string, readonly RouteObject[]>> =
  Object.fromEntries(
    Object.keys(CUSTOMER_MODULES)
      .sort()
      .map((key) => [groupOf(key), routesOf(CUSTOMER_MODULES[key] ?? {})] as const)
      // The runtime half of the guard described in the header. Not `Object.hasOwn`: this
      // runs while App.js is being imported, before React mounts, and the build targets
      // browsers (Chrome 87, Safari 14) that lack it. esbuild lowers syntax, never runtime
      // APIs, so a TypeError here would be a white screen no error element can catch.
      .filter(([name]) => !Object.prototype.hasOwnProperty.call(DEFERRED_GROUPS, name)),
  );

/**
 * The customer's groups the FOUNDER host also mounts: sign-in, and nothing else. A
 * founder reaches founder.oprappo.com signed out as often as not, and the door to the
 * Business OS must not send them to another host to find the login.
 */
export const FOUNDER_HOST_CUSTOMER_GROUPS: readonly string[] = ['onboarding'];

/** Where `/` goes on the founder host. */
export const FOUNDER_HOME = '/founder/enter';

/**
 * A Business OS path, opened on a customer host.
 *
 * `location.replace`, not `assign`: the customer-host URL is not a page anybody wants to
 * come back to, and leaving it in history would make Back bounce them forward again.
 * Done in an effect so the element itself stays a pure render — the tests render it and
 * read where it would go.
 */
export function FounderRedirect(): JSX.Element {
  const { pathname, search, hash } = useLocation();
  const target = `${FOUNDER_ORIGIN}${pathname}${search}${hash}`;
  useEffect(() => {
    window.location.replace(target);
  }, [target]);
  // A blank ground for the instant before the browser leaves. No text: nothing here is
  // for reading, and the customer's first load carries no Business OS copy at all.
  return <main data-redirect-to={target} aria-busy="true" className={FRAME_BOX + ' bg-sand'} />;
}

/**
 * A route the manifest names but no package has built.
 *
 * Deliberately not a skeleton, not a spinner and not a styled empty state.
 * docs/10 § rules: "Do not stub the design. A placeholder screen is worse than an
 * absent one, because it looks finished in review." This is a developer-facing
 * marker that cannot be mistaken for a screen, and the deploy gate fails while any
 * P0 route still resolves to it.
 */
/*
 * INLINE, AND IT HAS TO STAY INLINE — the one site where two of this repo's own rules
 * point opposite ways.
 *
 * Moving this to a class puts `font-family: monospace` into the built stylesheet, and
 * `npm run check:tokens` reads the built stylesheet: "Font family \"monospace\" is outside
 * the four. docs/01: Anek Bangla, Tiro Bangla, Manrope, Instrument Serif. Nothing else,
 * ever." Measured, not assumed — the class version fails the token lint.
 *
 * The monospace is the point of this element: it is a developer-facing marker that must
 * not be mistakable for a screen, and `styles.css` deliberately sends every other `<pre>`
 * to Manrope. So the choice is a class that fails a gate, a design change nobody asked
 * for, or an inline style. It stays inline, and `apps/web/public/_headers` § style-src
 * carries the reason. (`font-family: revert` would slip past the lint and render the same
 * monospace — that is gaming the checker, not satisfying it, and the checker's own header
 * says a blind checker is worse than none.)
 */
function NotBuilt({ id, label }: { id: string; label: string }) {
  return (
    <pre
      data-not-built={id}
      style={{
        font: '400 13px/1.75 monospace',
        padding: '16px',
        color: '#B3271E',
        background: '#F9F7F2',
        margin: 0,
      }}
    >
      {id} · {label} — not built. Owned by its work package; see docs/10.
    </pre>
  );
}

/**
 * Manifest paths with no built route yet, so the gap is visible rather than a 404.
 *
 * A path counts as claimed when ANY host mounts it, and a deferred group's paths count
 * exactly like an eager one's. They have to: the alternative is that `/founder/approvals`
 * is both a lazy route and a NotBuilt marker, the marker wins the match, and moving a
 * work package off the first load silently reports it as unbuilt.
 */
function pendingRoutes(): RouteObject[] {
  const claimed = new Set([
    ...collectPaths(Object.values(CUSTOMER_GROUPS).flat()),
    ...Object.values(DEFERRED_GROUPS).flatMap((g) => [...g.paths]),
  ]);
  return ROUTABLE.filter((s) => !claimed.has(normalise(s.route as string)))
    .map((s) => ({
      path: normalise(s.route as string),
      element: <NotBuilt id={s.id} label={s.label_bn} />,
    }));
}

/**
 * The manifest carries a query string on two entries (`/search?q=`) for the benefit
 * of the visual-regression harness, which needs to capture a populated frame. The
 * router wants the path alone.
 */
function normalise(route: string): string {
  return route.split('?')[0] ?? route;
}

function collectPaths(routes: RouteObject[], prefix = ''): string[] {
  const out: string[] = [];
  for (const r of routes) {
    const here = r.path === undefined ? prefix : joinPath(prefix, r.path);
    if (r.path !== undefined) out.push(here);
    if (r.children) out.push(...collectPaths(r.children, here));
  }
  return out;
}

function joinPath(prefix: string, path: string): string {
  if (path.startsWith('/')) return path;
  return `${prefix.replace(/\/$/, '')}/${path}`;
}

/**
 * Everything `host` mounts, under one pathless root that carries the error element.
 *
 * Exported so the tests can build a memory router per host and ask what each path
 * becomes; `App` builds the browser router from this same function, so what is tested is
 * what ships.
 */
export function routesFor(host: AppHost): RouteObject[] {
  const eager = Object.entries(CUSTOMER_GROUPS)
    .filter(([name]) => host !== 'founder' || FOUNDER_HOST_CUSTOMER_GROUPS.includes(name))
    .flatMap(([, routes]) => routes);

  const founderHome: RouteObject[] =
    host === 'founder' ? [{ path: '/', loader: () => redirect(FOUNDER_HOME) }] : [];

  const elsewhere: RouteObject[] = founderPathsFor(host).map((path) => ({
    path,
    element: <FounderRedirect />,
  }));

  return [
    {
      errorElement: <RouteError />,
      children: [
        ...founderHome,
        ...eager,
        ...deferredRoutes(host),
        ...elsewhere,
        ...pendingRoutes(),
        { path: '*', element: <NotFound /> },
      ],
    },
  ];
}

/**
 * Built on first render rather than at module scope: importing this file must not need a
 * browser, or the tests could not import it. One router per page load — StrictMode
 * renders twice and must not build two.
 */
let router: ReturnType<typeof createBrowserRouter> | undefined;

export function App() {
  router ??= createBrowserRouter(routesFor(appHost(window.location.hostname)));
  return <RouterProvider router={router} />;
}
